import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { CardLine } from 'components/List/StyledCard';
import { User, UserExistsByEmail } from 'types';
import { formatDateForDisplay } from 'utils/date';
import { findUserByEmail, postSortingCenterUserRoleMerge, putUserEmail } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { DialogUserExistsAndNotBelongToAdmin } from 'components/Forms/AdministrationUserForm/DialogUserExistsAndNotBelongToAdmin';
import { DialogUserExistsAndIsBelongToAdmin } from 'components/Forms/AdministrationUserForm/DialogUserExistsAndIsBelongToAdmin';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { NewStyledCard } from 'components/List/NewStyledCard';
import { UserInformationSchema } from './UserInformation.yup';
import { updateUrlWithForceRefresh } from '../../../utils/history';

type UserInformationProps = {
  user: User;
};

export const UserInformation: React.FC<UserInformationProps> = ({ user }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users.show.userInformation' });
  const history = useHistory();

  const [userInformation] = useState<User>(user);
  const createdAt = userInformation.createdAt ? formatDateForDisplay(userInformation.createdAt) : '-';

  const schema = useMemo(() => UserInformationSchema(t), [t]);
  const resolver = yupResolver(schema);

  const information: CardLine[] = [
    { key: 'lastName', label: t('lastName'), value: user.lastName },
    { key: 'firstName', label: t('firstName'), value: user.firstName },
    { key: 'email', label: t('email'), value: user.email, editable: true },
    { key: 'createdAt', label: t('createdAt'), value: createdAt },
  ];

  const [openDialogNotBelongToAdmin, setOpenDialogNotBelongToAdmin] = useState(false);
  const [openDialogIsBelongToAdmin, setOpenDialogIsBelongToAdmin] = useState(false);
  const [userFoundByEmail, setUserFoundByEmail] = useState<UserExistsByEmail | null>(null);
  const [preserveEditing, setPreserveEditing] = useState(false);

  const checkUserEmail = useCallback(
    (e: Partial<User>) => {
      if (e?.email === user.email) return;

      return findUserByEmail(e?.email ?? '')
        .then(userFound => {
          if (userFound?.user) {
            setUserFoundByEmail(userFound);
            if (!userFound.isBelongToAdministrator) {
              setOpenDialogNotBelongToAdmin(true);
            } else {
              setOpenDialogIsBelongToAdmin(true);
            }
          } else {
            handleSaveAction(e?.email ?? '');
          }
        })
        .catch(err => {
          notify({
            message: t('errorGeneric'),
            severity: 'error',
          });
          return;
        });
    }, // see ET-2546 for more informations
    [t, user.email], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleShowAction = () => {
    setOpenDialogIsBelongToAdmin(false);
    updateUrlWithForceRefresh(`/administration/users/${transformObjectId(userFoundByEmail?.user)}`, history);
  };

  const handleCancelAction = () => {
    setPreserveEditing(true);
    setOpenDialogIsBelongToAdmin(false);
    setOpenDialogNotBelongToAdmin(false);
  };

  const handleSaveAction = (email: string, fusion: boolean = false) => {
    setPreserveEditing(false);
    return putUserEmail(transformObjectId(user), email)
      .then(result => {
        notify({
          message: t('success'),
          severity: 'success',
        });
      })
      .catch(err => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      });
  };

  const handleMergeAction = (userFrom: User, userTo: UserExistsByEmail | null) => {
    setPreserveEditing(false);
    return postSortingCenterUserRoleMerge(userFrom, userTo)
      .then(userResult => {
        notify({
          message: t('success'),
          severity: 'success',
        });
        updateUrlWithForceRefresh(`/administration/users/${transformObjectId(userFoundByEmail?.user)}`, history);
      })
      .catch(err => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      });
  };

  return (
    <>
      <DialogUserExistsAndNotBelongToAdmin
        isOpen={openDialogNotBelongToAdmin}
        setOpen={setOpenDialogNotBelongToAdmin}
        userExistByEmail={userFoundByEmail}
        handleCancel={() => {
          handleCancelAction();
        }}
        handleContinue={() => {
          handleMergeAction(user, userFoundByEmail);
        }}
        labelContinue={t('validate')}
      />
      <DialogUserExistsAndIsBelongToAdmin
        isOpen={openDialogIsBelongToAdmin}
        setOpen={setOpenDialogIsBelongToAdmin}
        userExistByEmail={userFoundByEmail}
        handleCancel={() => {
          handleCancelAction();
        }}
        handleContinue={() => {
          handleShowAction();
        }}
        labelContinue={t('show')}
      />
      <Grid container>
        <Grid item xs={10}>
          <NewStyledCard
            title={t('title')}
            lines={information}
            editable={true}
            onSubmit={checkUserEmail}
            resolver={resolver}
            preserveEditing={preserveEditing}
          />
        </Grid>
      </Grid>
    </>
  );
};
